import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Download } from 'lucide-react';
import { StockSection } from '@/components/battery/StockSection';
import { InTransitSection } from '@/components/battery/InTransitSection';
import { ToOrderSection } from '@/components/battery/ToOrderSection';
import { DirectAllocationModal } from '@/components/battery/DirectAllocationModal';
import { CreateReservationModal } from '@/components/battery/CreateReservationModal';
import { ContainerModal } from '@/components/battery/ContainerModal';
import { AddBatteryModal } from '@/components/battery/AddBatteryModal';
import { AddStockBatteryModal } from '@/components/battery/AddStockBatteryModal';
import { AddToOrderBatteryModal } from '@/components/battery/AddToOrderBatteryModal';
import { EditContainerModal } from '@/components/battery/EditContainerModal';
import { EditBatteryModal } from '@/components/battery/EditBatteryModal';
import { DeleteConfirmDialog } from '@/components/battery/DeleteConfirmDialog';
import { useBatteryContainers } from '@/hooks/useBatteryContainers';
import { useBatteryAllocations } from '@/hooks/useBatteryAllocations';
import { useReservations, type Reservation } from '@/hooks/useReservations';
import { useToast } from '@/hooks/use-toast';
import type { Container, Battery } from '@/hooks/useBatteryContainers';

export default function CRMBatteryPlanner() {
  const [directAllocationModalOpen, setDirectAllocationModalOpen] = useState(false);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [containerModalOpen, setContainerModalOpen] = useState(false);
  const [addBatteryModalOpen, setAddBatteryModalOpen] = useState(false);
  const [addStockModalOpen, setAddStockModalOpen] = useState(false);
  const [addToOrderModalOpen, setAddToOrderModalOpen] = useState(false);
  const [editContainerModalOpen, setEditContainerModalOpen] = useState(false);
  const [editBatteryModalOpen, setEditBatteryModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'container' | 'battery'>('container');
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [selectedBattery, setSelectedBattery] = useState<Battery | null>(null);

  const {
    containers,
    batteries,
    batteryTypes,
    loading: containersLoading,
    createContainer,
    createBattery,
    createBatteries,
    updateContainer,
    deleteContainer,
    updateBattery,
    deleteBattery,
  } = useBatteryContainers();

  const {
    allocations,
    loading: allocationsLoading,
    refetch: refetchAllocations,
  } = useBatteryAllocations();

  const {
    reservations,
    createReservation,
    updateReservationStatus,
    deleteReservation,
  } = useReservations();

  const { toast } = useToast();

  // Create allocation map for display
  const allocationMap = new Map<string, string>();
  allocations.forEach((allocation) => {
    if (allocation.battery_id) {
      if (allocation.org_name) {
        allocationMap.set(allocation.battery_id, allocation.org_name);
      }
    }
  });

  // Filter batteries by section
  const stockBatteries = batteries.filter(b => b.status === 'vrij');
  const inTransitBatteries = batteries.filter(b => 
    containers.some(c => c.id === b.container_id)
  );
  
  // Get allocations per section
  const stockAllocations = allocations.filter(a => 
    a.battery_id && stockBatteries.some(b => b.id === a.battery_id)
  );
  const inTransitAllocations = allocations.filter(a => 
    a.battery_id && inTransitBatteries.some(b => b.id === a.battery_id)
  );

  const handleEditContainer = (container: Container) => {
    setSelectedContainer(container);
    setEditContainerModalOpen(true);
  };

  const handleDeleteContainer = (container: Container) => {
    setSelectedContainer(container);
    setDeleteType('container');
    setDeleteDialogOpen(true);
  };

  const handleEditBattery = (battery: Battery) => {
    setSelectedBattery(battery);
    setEditBatteryModalOpen(true);
  };

  const handleDeleteBattery = (battery: Battery) => {
    setSelectedBattery(battery);
    setDeleteType('battery');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === 'container' && selectedContainer) {
        await deleteContainer(selectedContainer.id);
        toast({ title: 'Container verwijderd' });
      } else if (deleteType === 'battery' && selectedBattery) {
        await deleteBattery(selectedBattery.id);
        toast({ title: 'Batterij verwijderd' });
      }
      setDeleteDialogOpen(false);
      setSelectedContainer(null);
      setSelectedBattery(null);
    } catch (error) {
      console.error('Error deleting:', error);
      toast({
        title: 'Fout',
        description: 'Er is een fout opgetreden bij het verwijderen',
        variant: 'destructive',
      });
    }
  };

  const handleAddStockBattery = async (data: {
    battery_type_id: string;
    serial_number?: string;
    notes?: string;
  }) => {
    try {
      const batteryType = batteryTypes.find(t => t.id === data.battery_type_id);
      if (!batteryType) return;

      await createBattery({
        battery_type_id: data.battery_type_id,
        power_kw: batteryType.power_kw,
        capacity_kwh: batteryType.capacity_kwh,
        status: 'vrij',
        serial_number: data.serial_number,
        notes: data.notes,
        container_id: null,
      });

      toast({
        title: 'Batterij toegevoegd',
        description: 'De batterij is succesvol toegevoegd aan voorraad',
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er is een fout opgetreden bij het toevoegen',
        variant: 'destructive',
      });
    }
  };

  const handleAddToOrderBattery = async (data: {
    battery_type_id: string;
    serial_number?: string;
    notes?: string;
  }) => {
    try {
      const batteryType = batteryTypes.find(t => t.id === data.battery_type_id);
      if (!batteryType) return;

      await createBattery({
        battery_type_id: data.battery_type_id,
        power_kw: batteryType.power_kw,
        capacity_kwh: batteryType.capacity_kwh,
        status: 'te_bestellen',
        serial_number: data.serial_number,
        notes: data.notes,
        container_id: null,
      });

      toast({
        title: 'Batterij toegevoegd',
        description: 'De batterij is succesvol toegevoegd aan te bestellen',
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er is een fout opgetreden bij het toevoegen',
        variant: 'destructive',
      });
    }
  };

  const handleConvertToStock = async (reservation: Reservation) => {
    const batteryType = batteryTypes.find(t => t.id === reservation.battery_type_id);
    if (!batteryType) return;

    // Create batteries for the reservation quantity
    for (let i = 0; i < reservation.quantity; i++) {
      await createBattery({
        battery_type_id: reservation.battery_type_id!,
        power_kw: batteryType.power_kw,
        capacity_kwh: batteryType.capacity_kwh,
        status: 'vrij',
        notes: `Geconverteerd vanuit reservering: ${reservation.organizations?.name || 'Onbekend'}`,
        container_id: null,
      });
    }

    // Delete the reservation
    await deleteReservation(reservation.id);

    toast({
      title: 'Geconverteerd',
      description: `${reservation.quantity} batterij(en) toegevoegd aan voorraad`,
    });
  };

  const loading = containersLoading || allocationsLoading;

  return (
    <div className="h-full flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Battery Planner</h1>
          <p className="text-muted-foreground">
            Beheer batterijvoorraad, onderweg containers en bestellingen
          </p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Exporteer
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 overflow-hidden">
          {/* Voorraad Section */}
          <StockSection
            batteries={stockBatteries}
            allocations={allocationMap}
            onAllocate={(battery) => {
              setSelectedBattery(battery);
              setDirectAllocationModalOpen(true);
            }}
            onEdit={handleEditBattery}
            onDelete={handleDeleteBattery}
            onAddStock={() => setAddStockModalOpen(true)}
          />

          {/* Onderweg Section */}
          <InTransitSection
            containers={containers}
            batteries={batteries}
            allocations={allocationMap}
            onAddContainer={() => setContainerModalOpen(true)}
            onAddBatteries={(container) => {
              setSelectedContainer(container);
              setAddBatteryModalOpen(true);
            }}
            onEditContainer={handleEditContainer}
            onDeleteContainer={handleDeleteContainer}
            onEditBattery={handleEditBattery}
            onDeleteBattery={handleDeleteBattery}
            onAllocateBattery={(battery) => {
              setSelectedBattery(battery);
              setDirectAllocationModalOpen(true);
            }}
            onBookToStock={async (container) => {
              try {
                await updateContainer(container.id, { status: 'aangekomen' });
                toast({
                  title: 'Container geboekt',
                  description: `${container.name} is in voorraad geboekt`,
                });
              } catch (error) {
                console.error('Error booking container:', error);
                toast({
                  title: 'Fout',
                  description: 'Er is een fout opgetreden bij het boeken',
                  variant: 'destructive',
                });
              }
            }}
          />

          {/* Te bestellen Section */}
          <ToOrderSection
            batteries={batteries}
            allocations={allocationMap}
            onAddToOrder={() => setAddToOrderModalOpen(true)}
            onAllocate={(battery) => {
              setSelectedBattery(battery);
              setDirectAllocationModalOpen(true);
            }}
            onEdit={handleEditBattery}
            onDelete={handleDeleteBattery}
          />
        </div>
      )}

      {/* Modals */}
      <DirectAllocationModal
        open={directAllocationModalOpen}
        onClose={() => {
          setDirectAllocationModalOpen(false);
          setSelectedBattery(null);
        }}
        onSuccess={() => {
          setDirectAllocationModalOpen(false);
          setSelectedBattery(null);
          refetchAllocations();
        }}
        batteryId={selectedBattery?.id || ''}
        batteryName={selectedBattery ? `${selectedBattery.power_kw}kW` : ''}
      />

      <CreateReservationModal
        open={reservationModalOpen}
        onClose={() => setReservationModalOpen(false)}
        onSuccess={() => setReservationModalOpen(false)}
      />

      <ContainerModal
        open={containerModalOpen}
        onClose={() => setContainerModalOpen(false)}
        onCreate={createContainer}
        batteryTypes={batteryTypes}
      />

      {selectedContainer && (
        <AddBatteryModal
          open={addBatteryModalOpen}
          onClose={() => {
            setAddBatteryModalOpen(false);
            setSelectedContainer(null);
          }}
          container={selectedContainer}
          batteryTypes={batteryTypes}
          existingBatteries={batteries}
          onAdd={async (typeId, qty) => {
            await createBatteries(selectedContainer.id, typeId, qty);
            setAddBatteryModalOpen(false);
            setSelectedContainer(null);
          }}
        />
      )}

      <EditContainerModal
        open={editContainerModalOpen}
        onClose={() => {
          setEditContainerModalOpen(false);
          setSelectedContainer(null);
        }}
        container={selectedContainer}
        onUpdate={updateContainer}
      />

      <EditBatteryModal
        open={editBatteryModalOpen}
        onClose={() => {
          setEditBatteryModalOpen(false);
          setSelectedBattery(null);
        }}
        battery={selectedBattery}
        onUpdate={updateBattery}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedContainer(null);
          setSelectedBattery(null);
        }}
        onConfirm={handleConfirmDelete}
        title={deleteType === 'container' ? 'Container verwijderen?' : 'Batterij verwijderen?'}
        description={
          deleteType === 'container'
            ? 'Weet je zeker dat je deze container wilt verwijderen? Alle batterijen in deze container worden ook verwijderd.'
            : 'Weet je zeker dat je deze batterij wilt verwijderen?'
        }
      />

      <AddStockBatteryModal
        open={addStockModalOpen}
        onOpenChange={setAddStockModalOpen}
        batteryTypes={batteryTypes}
        onSubmit={handleAddStockBattery}
      />

      <AddToOrderBatteryModal
        open={addToOrderModalOpen}
        onOpenChange={setAddToOrderModalOpen}
        batteryTypes={batteryTypes}
        onSubmit={handleAddToOrderBattery}
      />
    </div>
  );
}
